import { PRODUCT_ORDER_V1 } from '@oskecare/fe-utilities/lib/constants/dataSources.js';
import {
  CHARACTERISTIC_MASTER_SN,
  ORDER_ACTION_ADD,
  ORDER_ACTION_MODIFY,
  ORDER_ACTION_DELETE,
  ORDER_CATEGORIES,
  ORDER_CATEGORY_E_SIM,
  ORDER_CATEGORY_SHARING_SERVICE,
  ORDER_CHANNEL_WEB,
  ORDER_CHANNELS,
  PRODUCT_ID_MASTER_SN,
  PRODUCT_ID_SHS_UNPROCESSED_CHANGES,
  PRODUCT_ID_TOGETHER_FREE_OWNER,
  TARIFF_OPTION_ID_E_SIM_NEW,
  TARIFF_OPTION_ID_E_SIM_ROLLBACK,
  ORDER_ACTION_ADD_CHARACTERISTIC,
  ORDER_ACTION_DELETE_CHARACTERISTIC,
  CHARACTERISTIC_TOGETHER_FREE_MEMBER,
  ORDER_ITEM_RELATIONSHIP_TYPE_HAS_PREREQUISITE,
  ORDER_ITEM_RELATIONSHIP_TYPE_IS_PREREQUISITE,
  CHARACTERISTIC_TOGETHER_FREE_OWNER,
  PRODUCT_ID_TOGETHER_FREE_MEMBER,
  ORDER_CATEGORY_AUTOMATIC_REFILL,
  PRODUCT_ID_AUTOMATIC_REFILL,
  PRODUCT_ID_PREPAID_STARTER_PACK_DATA_OFFER,
} from './constants.js';

export default class Order {
  /**
   * @param {string} apiUrl
   * @param {function} resetCache
   * @param {function} request
   * @param {CustomerDataManager} customerDataManager
   */
  constructor({ apiUrl, resetCache, request, customerDataManager }) {
    this._apiUrl = apiUrl;
    this._resetCache = resetCache;
    this._request = request;
    this._customerDataManager = customerDataManager;
  }

  /**
   * @param {string} sn
   * @param {?string} billingAccountId
   * @param {?string} category
   * @param {?string} channel
   * @param {object[]} orderItems - structure of the order items may differ across services, refer to provided examples and/or documentation
   * @return {object}
   */
  static getOrderBody({
    sn,
    billingAccountId = null,
    category = null,
    channel = null,
    orderItems = [],
  }) {
    if (!sn) {
      throw new Error('SN must be specified.');
    }

    const body = {
      relatedPublicKey: [
        {
          name: 'customerMSISDN',
          id: sn,
        },
      ],
      orderItem: orderItems,
    };

    if (billingAccountId) {
      body.relatedParty = [
        {
          role: 'BillingAccount',
          id: billingAccountId,
        },
      ];
    }

    if (category) {
      if (!ORDER_CATEGORIES.includes(category)) {
        throw new Error(`Category must be one of: ${ORDER_CATEGORIES.join(', ')}`);
      }
      body.category = category;
    }

    if (channel) {
      if (!ORDER_CHANNELS.includes(channel)) {
        throw new Error(`Channel must be one of: ${ORDER_CHANNELS.join(', ')}`);
      }
      body.channel = [
        {
          id: channel,
        },
      ];
    }

    return body;
  }

  /**
   * @param {string} sn
   * @param {?string} billingAccountId
   * @param {?string} category
   * @param {?string} channel
   * @param {object[]} orderItems - structure of the order items may differ across services, refer to provided examples and/or documentation
   * @param {?object} headers
   * @param {number[]} expectedErrorCodes
   * @return {Promise<*>}
   * @private
   */
  _submitOrder({
    sn,
    billingAccountId = null,
    category = null,
    channel = null,
    orderItems = [],
    headers = null,
    expectedErrorCodes = [],
  }) {
    return this._request({
      method: 'POST',
      route: this._apiUrl,
      dataSource: PRODUCT_ORDER_V1,
      body: Order.getOrderBody({ sn, billingAccountId, category, channel, orderItems }),
      headers,
      expectedErrorCodes,
      isImportant: true,
    }).then(data => {
      this._resetCache();
      return data;
    });
  }

  /**
   * @param {string} sn
   * @param {?object} headers
   * @param {?(number[])} expectedErrorCodes
   * @return {Promise<*>}
   */
  submitESimActivation({ sn, headers, expectedErrorCodes }) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      category: ORDER_CATEGORY_E_SIM,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_ADD,
          product: {
            id: '1',
            characteristic: [
              {
                name: 'simTypeId',
                value: 30,
              },
            ],
            productSpecification: {
              id: TARIFF_OPTION_ID_E_SIM_NEW,
            },
          },
        },
      ],
      headers,
      expectedErrorCodes,
    });
  }

  /**
   * @param {string} sn
   * @param {string} lastFourDigits
   * @param {?object} headers
   * @param {?(number[])} expectedErrorCodes
   * @return {Promise<*>}
   */
  submitESimRollback({ sn, lastFourDigits, headers, expectedErrorCodes }) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      category: ORDER_CATEGORY_E_SIM,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_ADD,
          product: {
            id: '1',
            characteristic: [
              {
                name: 'rollbackSimId',
                value: lastFourDigits,
              },
            ],
            productSpecification: {
              id: TARIFF_OPTION_ID_E_SIM_ROLLBACK,
            },
          },
        },
      ],
      headers,
      expectedErrorCodes,
    });
  }

  /**
   * @param {string} sn
   * @param {object[]} orderItems
   * @return {Promise<*>}
   */
  submitSharingService(sn, orderItems) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      category: ORDER_CATEGORY_SHARING_SERVICE,
      orderItems,
    });
  }

  /**
   * @param {string} sn
   * @param {string} groupId
   * @return {Promise<*>}
   */
  cancelSharingServiceChanges(sn, groupId) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      category: ORDER_CATEGORY_SHARING_SERVICE,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_DELETE,
          product: {
            id: groupId,
            productSpecification: {
              id: PRODUCT_ID_SHS_UNPROCESSED_CHANGES,
            },
          },
        },
      ],
    });
  }

  /**
   * @param {string} sn
   * @param {?string} masterSn
   * @return {Promise<*>}
   */
  async submitMasterSn({ sn, masterSn }) {
    const { billingAccountId } = (await this._customerDataManager.getCustomerData()).cnData;
    return this._submitOrder({
      sn,
      billingAccountId,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_MODIFY,
          product: {
            id: '1',
            characteristic: [
              {
                name: CHARACTERISTIC_MASTER_SN,
                value: (masterSn ?? '').replace(/^421/, '0'),
              },
            ],
            productSpecification: {
              id: PRODUCT_ID_MASTER_SN,
            },
          },
        },
      ],
    });
  }

  /**
   * Add a member to working user's group
   *
   * @param {string} sn - working SN
   * @param {string} memberSn - SN which is to be added to the group
   * @return {Promise<*>}
   */
  submitTogetherFreeAddMember({ sn, memberSn }) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_ADD_CHARACTERISTIC,
          product: {
            id: '1',
            characteristic: [{ name: CHARACTERISTIC_TOGETHER_FREE_MEMBER, value: memberSn }],
            productSpecification: { id: PRODUCT_ID_TOGETHER_FREE_OWNER },
          },
        },
      ],
      expectedErrorCodes: [422],
    });
  }

  /**
   * Remove a member from working user's group
   *
   * @param {string} sn - working SN
   * @param {string} memberSn - SN which is to be removed from the group
   * @return {Promise<*>}
   */
  submitTogetherFreeRemoveMember({ sn, memberSn }) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_DELETE_CHARACTERISTIC,
          product: {
            id: '1',
            characteristic: [{ name: CHARACTERISTIC_TOGETHER_FREE_MEMBER, value: memberSn }],
            productSpecification: { id: PRODUCT_ID_TOGETHER_FREE_OWNER },
          },
        },
      ],
      expectedErrorCodes: [422],
    });
  }

  /**
   * Replace one TF member with another in working user's group
   *
   * @param {string} sn - working SN
   * @param {string} previousMemberSn - SN which is to be removed from the group
   * @param {string} newMemberSn - SN which is to be added to the group
   * @return {Promise<*>}
   */
  submitTogetherFreeReplaceMember({ sn, previousMemberSn, newMemberSn }) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_DELETE_CHARACTERISTIC,
          product: {
            id: '1',
            characteristic: [
              { name: CHARACTERISTIC_TOGETHER_FREE_MEMBER, value: previousMemberSn },
            ],
            productSpecification: { id: PRODUCT_ID_TOGETHER_FREE_OWNER },
          },
          orderItemRelationship: { type: ORDER_ITEM_RELATIONSHIP_TYPE_IS_PREREQUISITE, id: '2' },
        },
        {
          id: '2',
          action: ORDER_ACTION_ADD_CHARACTERISTIC,
          product: {
            id: '1',
            characteristic: [{ name: CHARACTERISTIC_TOGETHER_FREE_MEMBER, value: newMemberSn }],
            productSpecification: { id: PRODUCT_ID_TOGETHER_FREE_OWNER },
          },
          orderItemRelationship: { type: ORDER_ITEM_RELATIONSHIP_TYPE_HAS_PREREQUISITE, id: '1' },
        },
      ],
      expectedErrorCodes: [422],
    });
  }

  /**
   * Remove self (working SN) from another persons TF group
   *
   * @param {string} sn - ownerSn
   * @param {string} ownerSn - SN which owns the group from which working user wants to be removed from
   * @return {Promise<*>}
   */
  submitTogetherFreeRemoveSelf({ sn, ownerSn }) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_DELETE,
          product: {
            id: '1',
            characteristic: [{ name: CHARACTERISTIC_TOGETHER_FREE_OWNER, value: ownerSn }],
            productSpecification: { id: PRODUCT_ID_TOGETHER_FREE_MEMBER },
          },
        },
      ],
      expectedErrorCodes: [422],
    });
  }

  /**
   * @param {string} sn
   * @param {string} action
   * @param {object[]} characteristics
   * @return {Promise<*>}
   */
  submitAutomaticRefill({ sn, action = ORDER_ACTION_ADD, characteristics }) {
    return this._submitOrder({
      sn,
      category: ORDER_CATEGORY_AUTOMATIC_REFILL,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action,
          product: {
            id: '1',
            productSpecification: { id: PRODUCT_ID_AUTOMATIC_REFILL },
            characteristic: characteristics,
          },
        },
      ],
      expectedErrorCodes: [422],
    });
  }

  /**
   * @param {string} sn
   * @return {Promise<*>}
   */
  submitPrepaidStarterPackDataOffer(sn) {
    return this._submitOrder({
      sn,
      channel: ORDER_CHANNEL_WEB,
      orderItems: [
        {
          id: '1',
          action: ORDER_ACTION_ADD,
          product: {
            id: '1',
            productSpecification: { id: PRODUCT_ID_PREPAID_STARTER_PACK_DATA_OFFER },
          },
        },
      ],
    });
  }
}
